// What a meeting's state means to the operator, in words: the status badge,
// the recording and notes-bot badges, the follow-up note and which tab it
// belongs to. One definition for the desktop Meetings page, the companion's
// Meetings and Today screens, so the same meeting reads the same
// everywhere. "Needs you" is the shared attention predicate, so the sidebar
// badge, Home and these screens always show the same number.
//
// Pure functions, no React. Self-test: status.selftest.ts.

import type { Meeting } from "@/api/meetings";
import { ASKED_WAIT_DAYS, meetingAskedNoReply, meetingNeedsYou } from "@/features/attention/predicates";
import { daysBetween, formatDateTime, formatDay, isDateOnly, joinList, pluralize, toDate } from "@/lib/format";

export type MeetingTone = "neutral" | "info" | "warning" | "success" | "danger";
export type MeetingTab = "needs" | "upcoming" | "done" | "dismissed";

export interface MeetingStatus {
  label: string;
  tone: MeetingTone;
  /** Longer explanation for a tooltip. */
  hint?: string;
}

/**
 * Mirrors the backend's worth_prompting (services/meetings.py): the agent
 * only asks about a meeting that was recorded, or had someone else in it,
 * or had a video link. Solo blocks ("Focus time") are never asked about.
 */
export function worthPrompting(m: Pick<Meeting, "recording_path" | "transcript_path" | "attendees" | "meet_link">): boolean {
  if (m.recording_path || m.transcript_path) return true;
  if (m.attendees.some((a) => !a.self && a.email)) return true;
  return !!m.meet_link;
}

/**
 * Ended, and nobody else was in it: it counts under Needs you (the shared
 * rule) but the agent will never ask about it, so the screens list it
 * apart from the meetings that really wait on the operator.
 */
export function isSoloEnded(m: Meeting): boolean {
  return m.status === "ended" && !worthPrompting(m);
}

/** A recording is still being uploaded or transcribed; its notes are on the way. */
export function recordingInFlight(m: Pick<Meeting, "recording_status">): boolean {
  return m.recording_status === "uploading" || m.recording_status === "transcribing";
}

// A first.last email handle standing in for a name ("ravjit.johar",
// "jean-luc_picard"). A hyphen alone is part of real names ("Anne-Marie").
const HANDLE = /^[a-z]+(?:-[a-z]+)*(?:[._][a-z]+(?:-[a-z]+)*)+$/i;
const cap = (w: string) => w.split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join("-");

// Display only. Calendars write some names as "Parshottam, Jay"; joined
// with commas that reads as two people. A first.last handle reads as a
// name ("Ravjit Johar"); opaque handles ("aknx143") stay as they are.
export function personName(n: string): string {
  if (HANDLE.test(n)) return n.split(/[._]/).map(cap).join(" ");
  return n.replace(/^([^,]+),\s*([^,]+)$/, "$2 $1");
}

/** The other people in the meeting (names, else the part of the email before @). */
export function otherAttendees(m: Pick<Meeting, "attendees">): string[] {
  return m.attendees.filter((a) => !a.self && a.email).map((a) => personName(a.name?.trim() || a.email.split("@")[0]));
}

/** The other people's email addresses, for a tooltip (the names shown may be tidied handles). */
export function attendeeEmails(m: Pick<Meeting, "attendees">): string {
  return m.attendees.filter((a) => !a.self && a.email).map((a) => a.email).join(", ");
}

/** "with A, B and C" / "with A, B and 3 others" (more than max + 1 people); "" when nobody else. */
export function withPeople(m: Pick<Meeting, "attendees">, max = 2): string {
  const who = otherAttendees(m);
  if (!who.length) return "";
  if (who.length <= max + 1) return `with ${joinList(who)}`;
  return `with ${who.slice(0, max).join(", ")} and ${pluralize(who.length - max, "other")}`;
}

/** The subtitle leaves people out when more than max + 1 others were there. */
export const subtitleShortened = (m: Pick<Meeting, "attendees">, max = 2) => otherAttendees(m).length > max + 1;

/** "with Ido Garbi, Alex Coggins and 5 others" / "Recorded in the app" / "No other attendees". */
export function meetingSubtitle(m: Pick<Meeting, "attendees" | "source">, max = 2): string {
  const who = withPeople(m, max);
  if (who) return who;
  if (m.source === "recording") return "Recorded in the app";
  if (m.source === "bot") return "Notes bot recording";
  return "No other attendees";
}

/** Meeting titles arrive with stray whitespace from the calendar. */
export const meetingTitle = (m: Pick<Meeting, "title">) => m.title.trim() || "Untitled meeting";

/** An upcoming meeting: in progress, starting soon, or how far ahead. */
function upcomingStatus(m: Meeting, now: Date): MeetingStatus {
  const start = toDate(m.start_at);
  const end = toDate(m.end_at);
  if (!start) return { label: "Upcoming", tone: "neutral" };
  const allDay = isDateOnly(m.start_at);
  if (start <= now && (!end || now < end)) return { label: allDay ? "Today" : "In progress", tone: "info" };
  // Over, and the calendar sync has not caught up yet.
  if (end && end <= now) return { label: "Ended", tone: "neutral" };
  const mins = Math.round((start.getTime() - now.getTime()) / 60_000);
  if (!allDay && mins >= 0 && mins < 60) return { label: `Starts in ${Math.max(1, mins)} min`, tone: "info" };
  const days = daysBetween(now, start);
  if (days <= 0) return { label: "Later today", tone: "neutral" };
  if (days === 1) return { label: "Tomorrow", tone: "neutral" };
  return { label: `In ${days} days`, tone: "neutral" };
}

export interface StatusOptions {
  now?: Date;
  /** The agent's display name, for the hints. */
  agentName?: string;
}

/**
 * The meeting's own status badge, from m.status (and, for a skipped one,
 * m.skip_reason: a booking its booker cancelled from a scheduling page
 * reads "Cancelled by the booker", never the operator's "Dismissed").
 * Recording states are a separate badge (recordingStatus), so a failed
 * transcription never hides "Dismissed" or "Waiting for your notes". The
 * same words on the desktop and the phone, with one gap until the iPhone's
 * MeetingRules is ported: the phone still says "Dismissed" for a booking
 * its booker cancelled.
 *
 * Tones: warning = the operator owes something (the agent asked); info =
 * the agent's move next (not asked yet, in progress); success = done.
 */
export function meetingStatus(m: Meeting, opts: StatusOptions = {}): MeetingStatus {
  const agent = opts.agentName || "Your agent";
  switch (m.status) {
    case "upcoming":
      return upcomingStatus(m, opts.now ?? new Date());
    case "prompted":
      return { label: "Waiting for your notes", tone: "warning", hint: `${agent} asked about this meeting in chat` };
    case "ended":
      return worthPrompting(m)
        ? { label: "Not asked yet", tone: "info", hint: `Ended; ${agent} has not asked about it yet` }
        : { label: "Ended", tone: "neutral", hint: `Nobody else was in it, so ${agent} will not ask about it` };
    case "processed":
      return { label: "Notes done", tone: "success" };
    case "skipped":
      if (m.skip_reason === "booker_cancelled") {
        return { label: "Cancelled by the booker", tone: "neutral", hint: "The booker cancelled it with the change-or-cancel link from their scheduling-page invite" };
      }
      return { label: "Dismissed", tone: "neutral", hint: "Dismissed, marked as nothing to follow up, or cancelled on the calendar" };
  }
}

/**
 * The status word a list row shows for a meeting today: meetingStatus,
 * except a question the agent asked over ASKED_WAIT_DAYS days ago that
 * nobody answered. That one no longer waits on you (it is in no count), so
 * it says so quietly: "No reply · asked Fri 9 Oct", neutral. The app's
 * MeetingStatusLine says the same.
 */
export function meetingStatusToday(m: Meeting, opts: StatusOptions = {}): MeetingStatus {
  const now = opts.now ?? new Date();
  if (!meetingAskedNoReply(m, now)) return meetingStatus(m, opts);
  const agent = opts.agentName || "Your agent";
  const day = formatDay(m.prompted_at, now, { inline: true });
  return {
    label: day ? `No reply · asked ${day}` : "No reply",
    tone: "neutral",
    hint: `${agent} asked about this meeting in chat over ${pluralize(ASKED_WAIT_DAYS, "day")} ago and nobody replied`,
  };
}

/** A second badge for a recording made in the app, or null when there is nothing to say. */
export function recordingStatus(m: Pick<Meeting, "recording_status" | "recording_error">): MeetingStatus | null {
  switch (m.recording_status) {
    case "uploading": return { label: "Recording…", tone: "info" };
    case "transcribing": return { label: "Transcribing", tone: "info" };
    case "failed": return { label: "Transcription failed", tone: "danger", hint: m.recording_error ?? undefined };
    default: return null;
  }
}

/** A badge for the notes bot, or null when none was involved. */
export function botStatus(m: Pick<Meeting, "bot">): MeetingStatus | null {
  switch (m.bot) {
    case "requested": return { label: "Notes bot booked", tone: "info" };
    case "joined": return { label: "Notes bot in the call", tone: "info" };
    case "done": return { label: "Bot transcript", tone: "neutral" };
    case "failed": return { label: "Notes bot failed", tone: "danger" };
    default: return null;
  }
}

/** Which tabs the meeting shows under. Notes done with open actions is in both Needs you and Done. */
export function inTab(m: Meeting, tab: MeetingTab): boolean {
  switch (tab) {
    case "needs": return meetingNeedsYou(m);
    case "upcoming": return m.status === "upcoming";
    case "done": return m.status === "processed";
    case "dismissed": return m.status === "skipped";
  }
}

/** Safe to move to Dismissed in bulk: nobody wrote it up and no recording is still in flight. */
export function canDismiss(m: Meeting): boolean {
  return (m.status === "ended" || m.status === "prompted") && !recordingInFlight(m);
}

export const openActionCount = (m: Pick<Meeting, "actions">) => m.actions.filter((a) => a.status === "open").length;

const CADENCE: Record<string, string> = {
  daily: "asks once a day about meetings without notes",
  each: "asks after each meeting",
  off: "does not ask",
};

/** How an agent's follow-ups are set, as a verb phrase ("asks after each meeting"). */
export function cadencePhrase(followups: string | undefined): string {
  return CADENCE[followups ?? "daily"] ?? CADENCE.daily;
}

/** What the agent will do about one meeting that has not been asked about. */
function askNote(agentName: string, followups: string | undefined, ended: boolean): string {
  const cadence = followups ?? "daily";
  if (cadence === "off") return `${agentName} has follow-ups off`;
  if (cadence === "each") return ended ? `${agentName} asks shortly` : `${agentName} asks when it ends`;
  return `${agentName} asks in the daily round-up`;
}

/** The server cuts `recording_error` to this many characters (services/recordings/segments.py `error`). */
export const RECORDING_ERROR_MAX = 400;
/** The server's words after a part it still tries again by itself (segments.py `error`). */
export const AUTO_RETRY_WORDS = "(Corvus tries again by itself)";
/** Reasons a Retry by hand cannot help: nothing to hear, nothing to read (segments.py PLAIN_REASONS no_speech, no_recording). */
const NOTHING_TO_RETRY = /no speech was heard|its audio is missing/i;

export interface RecordingErrorShown {
  /** As a sentence: a capital and a full stop; a note the server cut short ends in "…" after its last whole part. */
  text: string;
  /** Retry by hand helps: a part failed for a reason a retry can fix, and Corvus no longer tries it by itself. */
  retry: boolean;
  /** Corvus still tries some part again by itself. */
  again: boolean;
}

/**
 * How a recording's `recording_error` is shown, the same on every surface
 * (the meeting page, the phone's meeting item, the iPhone: shared fixtures):
 * a sentence, and whether Retry transcription is offered. Never beside
 * "Corvus tries again by itself" (the button contradicted it), nor for no
 * speech or missing audio (a retry runs the same silence again). The server
 * cuts the text at RECORDING_ERROR_MAX: a part cut inside the suffix is one
 * Corvus tries again; a part cut inside its reason is judged by what is left
 * (Retry stays unless it says nothing can be heard). Neither is shown.
 */
export function recordingErrorShown(error: string | null | undefined): RecordingErrorShown | null {
  const raw = error ?? "";
  const t = raw.trim();
  if (!t) return null;
  const cut = raw.length >= RECORDING_ERROR_MAX;
  const parts = t.split(/;\s*/);
  const last = cut && parts.length > 1 ? parts.pop()! : null;
  const auto = (p: string) => p.includes(AUTO_RETRY_WORDS);
  // The part cut short: inside the suffix, one Corvus tries again; otherwise judged by what is left of it.
  const cutInSuffix = last !== null && /\([^)]*$/.test(last);
  const again = parts.some(auto) || cutInSuffix;
  const judged = last !== null && !cutInSuffix ? [...parts, last] : parts;
  const retry = judged.some((p) => !auto(p) && !NOTHING_TO_RETRY.test(p));
  const body = parts.join("; ").replace(/[.\s]+$/, (end) => (cut ? "" : end));
  const text = `${body[0].toUpperCase()}${body.slice(1)}${cut ? "…" : /[.!?]$/.test(body) ? "" : "."}`;
  return { text, retry, again };
}

/** What happens next with a recording whose transcription failed (the Follow-up column, the empty notes). */
export function failedRecordingNote(error: string | null | undefined): string {
  const shown = recordingErrorShown(error);
  if (!shown || shown.retry) return "Retry the transcription to get notes";
  return shown.again ? "Corvus tries the transcription again by itself" : "Nothing in it could be transcribed";
}

/**
 * The Follow-up column: what happens next with this meeting, from what the
 * API records (no prompt count and no dismissal time exist, so never
 * "asked twice" or "dismissed on …"). Every status has something to say.
 */
export function followupNote(m: Meeting, ctx: { agentName: string; followups?: string; now?: Date }): string {
  const open = openActionCount(m);
  const asked = () => formatDateTime(m.prompted_at, { inline: true }, ctx.now);
  if (m.status !== "skipped") {
    if (recordingInFlight(m)) return "Notes follow the transcript";
    if (m.recording_status === "failed") return failedRecordingNote(m.recording_error);
  }
  switch (m.status) {
    case "processed":
      return open ? pluralize(open, "open action") : "All actions done";
    case "prompted": {
      const at = asked();
      return at ? `Asked ${at}` : "Asked in chat";
    }
    case "ended":
      if (m.transcript_path || m.recording_status === "ready") return "Transcript ready";
      return worthPrompting(m) ? askNote(ctx.agentName, ctx.followups, true) : "Just you, so no follow-up";
    case "upcoming":
      if (m.bot === "requested") return "Notes bot will join";
      if (m.bot === "joined") return "Notes bot is taking notes";
      return worthPrompting(m) ? askNote(ctx.agentName, ctx.followups, false) : "Just you, so no follow-up";
    case "skipped": {
      if (open) return pluralize(open, "open action");
      const day = formatDay(m.prompted_at, ctx.now, { inline: true });
      if (day) return `Asked ${day}, then dismissed`;
      return worthPrompting(m) ? "Not followed up" : "Just you, so no follow-up";
    }
  }
}

/** A meeting action's due text: a real date reads as a day ("Tomorrow", "Tue 22 Sep"); free text stays as typed. */
export function formatDueText(due: string | null | undefined, now?: Date): string {
  if (!due) return "";
  const d = toDate(due);
  return d ? formatDay(d, now, { inline: true }) : due;
}

/** An action owner as written by the agent; "me"/"operator" is the operator. */
export function formatOwner(owner: string | null | undefined): string {
  const o = (owner ?? "").trim();
  if (!o) return "";
  return /^(me|operator|you)$/i.test(o) ? "You" : o;
}

const ACTION_KIND: Record<string, string> = { task: "Task", reminder: "Reminder", email: "Email", calendar: "Calendar", booking: "Booking" };
export const actionKindLabel = (kind: string) => ACTION_KIND[kind] ?? kind;

// Start (else when it was logged) as epoch ms; offsets differ between rows,
// so strings cannot be compared directly.
const startMs = (m: Pick<Meeting, "start_at" | "created_at">) => toDate(m.start_at ?? m.created_at)?.getTime() ?? 0;

/** Newest first. */
export function byStartDesc(a: Meeting, b: Meeting): number {
  return startMs(b) - startMs(a);
}

/** Soonest first; used for Upcoming. */
export function byStartAsc(a: Meeting, b: Meeting): number {
  return startMs(a) - startMs(b);
}

/**
 * The phone's "Needs you" list in order: recordings still being
 * transcribed first (they are NOT in the count - the shared rule does not
 * include them - but the operator just made them), then the meetings that
 * really wait on the operator, newest first, then solo blocks.
 */
export function needsYouList(all: readonly Meeting[]): { inFlight: Meeting[]; needs: Meeting[]; solo: Meeting[] } {
  const needsAll = all.filter(meetingNeedsYou).sort(byStartDesc);
  return {
    inFlight: all.filter((m) => recordingInFlight(m) && !meetingNeedsYou(m)).sort(byStartDesc),
    needs: needsAll.filter((m) => !isSoloEnded(m)),
    solo: needsAll.filter(isSoloEnded),
  };
}
